import React from "react";
import { useLocation, useNavigate } from "react-router";
import { joinClasses } from "./ResultsPrimitives.jsx";
import {
  applyDispatchPeriodSearchParam,
  normalizeDispatchPeriod,
  readDispatchDateFromSearch,
  readDispatchMonthFromSearch,
  readDispatchPeriodFromSearch,
  writeLastDispatchContext,
} from "../utils/dispatchDateUrls.js";

export function DispatchPeriodToggle({ value = "day", onChange }) {
  const activeValue = normalizeDispatchPeriod(value);
  const options = [
    { label: "Jour", value: "day" },
    { label: "Soir", value: "evening" },
  ];

  return (
    <div
      className="inline-grid h-8 grid-cols-2 overflow-hidden rounded-[4px] border border-[color:var(--color-accent)]/20 bg-white p-0.5 shadow-none"
      role="group"
      aria-label="Session de dispatch"
      data-dispatch-period-toggle="true"
    >
      {options.map((option) => {
        const isActive = activeValue === option.value;
        return (
          <button
            key={option.value}
            type="button"
            className={joinClasses([
              "inline-flex min-w-[3.75rem] items-center justify-center rounded-[3px] px-2.5 text-[11px] font-extrabold transition-colors duration-150",
              isActive
                ? "bg-[var(--color-accent)] text-white shadow-none"
                : "text-slate-600 hover:bg-slate-100 hover:text-slate-950",
            ])}
            aria-pressed={isActive}
            onClick={() => onChange?.(option.value)}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export function ConnectedDispatchPeriodToggle() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatchPeriod = React.useMemo(
    () => readDispatchPeriodFromSearch(location.search),
    [location.search],
  );

  const handleChange = React.useCallback((nextPeriod) => {
    const normalizedPeriod = normalizeDispatchPeriod(nextPeriod);
    if (normalizedPeriod === dispatchPeriod) {
      return;
    }

    const safeDispatchDate = readDispatchDateFromSearch(location.search);
    const dispatchMonthFromSearch = readDispatchMonthFromSearch(location.search);
    writeLastDispatchContext({
      dispatch_date: safeDispatchDate,
      dispatch_month: safeDispatchDate ? safeDispatchDate.slice(0, 7) : dispatchMonthFromSearch,
      dispatch_period: normalizedPeriod,
    });

    const nextSearchParams = new URLSearchParams(location.search);
    applyDispatchPeriodSearchParam(nextSearchParams, normalizedPeriod);
    if (safeDispatchDate) {
      nextSearchParams.set("dispatch_date", safeDispatchDate);
      nextSearchParams.set("dispatch_month", safeDispatchDate.slice(0, 7));
    }

    navigate(
      {
        pathname: location.pathname,
        search: nextSearchParams.toString() ? `?${nextSearchParams.toString()}` : "",
      },
      { replace: false },
    );
  }, [dispatchPeriod, location.pathname, location.search, navigate]);

  return (
    <DispatchPeriodToggle
      value={dispatchPeriod}
      onChange={handleChange}
    />
  );
}

export default DispatchPeriodToggle;
